import React, { useEffect } from 'react'
import { Reward } from '@vrw/data/src/redux/rewards/types'
import { RewardEarn as RewardEarnTile } from '../TileLister/RewardsEarnTile/convertCmsEarnsToTiles'
import { useGetStore } from '@/redux/hooks/hooks'
import { TileLister } from '../TileLister'
import formatEarnTiles from '../TileLister/RewardsEarnTile/formatEarnTiles'
import { formatRewardTiles } from '../TileLister/RewardsTile/formatRewardTiles'
import { RewardEarn } from '@vrw/data/src/redux/rewardsEarn/types'
import { LoadSpinner } from '..'
import { Text, View, VisuallyHidden } from '@red-ui/components'
import { useFavourites } from '../../mutations/rewards/useFavourites'
import { InfiniteData } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { reduceToPageCount } from '@/query/rewards/useInfiniteRewards'
import { flatten, map } from 'lodash/fp'

export type RewardsListProps = {
  rewards: InfiniteData<RewardEarn[] | Reward[]> | undefined
  isLoading: boolean
  fetchNextPage: () => void
  isFetching: boolean
  hasNextPage: boolean
}

const isRewardEarnType = (reward: Reward | RewardEarn): reward is RewardEarn => {
  return 'type' in reward && reward.type?.toLowerCase() === 'earn'
}

const rewardToTile = (reward: Reward | RewardEarn) =>
  isRewardEarnType(reward) ? formatEarnTiles(reward as RewardEarnTile) : formatRewardTiles(reward)

export const RewardsList = ({ rewards, isLoading, fetchNextPage, isFetching, hasNextPage }: RewardsListProps) => {
  const { ref: bottomInViewRef, inView: bottomInView } = useInView()
  const wishlist = useGetStore('wishlist.wishlist')
  const isLoadingWishlist = useGetStore('wishlist.isLoading')
  const isAuthenticated = useGetStore('auth.isAuthenticated')
  const { addToFavourites, deleteFromFavourites } = useFavourites()

  useEffect(() => {
    if (bottomInView && !isFetching && hasNextPage) fetchNextPage()
  }, [bottomInView, isFetching, rewards, hasNextPage, fetchNextPage])

  const tiles = map(rewardToTile, flatten((rewards?.pages as []) ?? []))

  return isLoading ? (
    <View justifyContent="center" flex={1} alignItems="center" minHeight={500}>
      <LoadSpinner />
    </View>
  ) : (
    <>
      <VisuallyHidden tag="output">
        <Text>{`${reduceToPageCount(rewards?.pages)} rewards available`}</Text>
      </VisuallyHidden>
      <TileLister
        tiles={tiles}
        showWishListIcons={isAuthenticated}
        wishlist={wishlist}
        isLoadingWishlist={isLoadingWishlist}
        doAddRewardToWishlist={(tile, type) => addToFavourites({ tile, type })}
        doRemoveRewardFromWishlist={(tile, type) => deleteFromFavourites({ tile, type })}
      />
      <div ref={bottomInViewRef} />
    </>
  )
}
